import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';

import { buildFilterUrl } from 'support/helpers/urls/urls';
import { FilterValue, PropertyFilter } from 'support/types/filters';

export const getFilterConfig = (
  defaultFilterConfig: Array<PropertyFilter>,
  searchParams: URLSearchParams,
): Array<PropertyFilter> =>
  defaultFilterConfig.map((propertyFilter: PropertyFilter) => {
    const searchParam = searchParams.get(`filter[${propertyFilter.key}]`);

    return {
      ...propertyFilter,
      ...(searchParam && { value: searchParam }),
    };
  });

export const getFilterValues = (filterConfig: Array<PropertyFilter>): Array<FilterValue> =>
  filterConfig.filter((filter) => filter.value.length).map((filter) => ({ key: filter.key, value: filter.value }));

export const useNewFilters = (
  defaultFilterConfig: Array<PropertyFilter>,
  key: string,
  // The user's saved default filters for this page, applied on a fresh open (no URL/session filters
  // yet). `undefined` means "not known yet" (e.g. still loading) so seeding waits for it.
  savedDefaultFilters?: Array<FilterValue>,
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filterValues, setFilterValues] = useSessionStorage<Array<FilterValue>>(
    key,
    getFilterValues(getFilterConfig(defaultFilterConfig, searchParams)),
  );

  const hasSeededDefault = useRef(false);

  const currentCursor = useMemo<string | null>(() => searchParams.get('cursor'), [searchParams]);
  const currentPerPage = useMemo<string | null>(() => searchParams.get('perPage') ?? null, [searchParams]);

  const searchParamsValue = searchParams.toString();

  const skipEffectsAfterUpdate = useRef(false); // solves issue where a filter with default values reappears immediately after being cleared

  useEffect(() => {
    if (skipEffectsAfterUpdate.current) {
      return;
    }
    setFilterValues(getFilterValues(getFilterConfig(defaultFilterConfig, searchParams)));
  }, [defaultFilterConfig, searchParamsValue, setFilterValues]);

  const updateUrlParams = useCallback(
    (
      filterValues: Array<FilterValue>,
      cursor: string | null,
      perPage: string | null,
      options?: { replace?: boolean },
    ) => {
      skipEffectsAfterUpdate.current = true;
      const params = buildFilterUrl({ filterValues, cursor, perPage });
      const newUrl = `${window.location.pathname}${params ?? ''}`;

      navigate(newUrl, options?.replace ? { replace: true } : undefined);
    },
    [navigate],
  );

  // Apply the user's saved default whenever the list is opened without explicit filters in the URL
  // (fresh load or reload). Runs once per mount, and only once the saved default is known. Uses
  // `replace` so it adds no history entry. Filters already in the URL always win (shared links).
  useEffect(() => {
    if (hasSeededDefault.current || savedDefaultFilters === undefined) {
      return;
    }
    hasSeededDefault.current = true;

    const urlHasFilters = searchParams.toString().includes('filter');
    if (urlHasFilters || savedDefaultFilters.length === 0) {
      return;
    }

    setFilterValues(savedDefaultFilters);
    updateUrlParams(savedDefaultFilters, null, currentPerPage, { replace: true });
  }, [savedDefaultFilters, searchParams, setFilterValues, updateUrlParams, currentPerPage]);

  useEffect(() => {
    if (skipEffectsAfterUpdate.current) {
      skipEffectsAfterUpdate.current = false;
      return;
    }
    if (!searchParams.toString().includes('filter')) {
      updateUrlParams(filterValues, null, currentPerPage);
    }
  }, [filterValues, currentPerPage, updateUrlParams, searchParams]);

  const filters = useMemo(
    () => getFilterConfig(defaultFilterConfig, searchParams),
    [defaultFilterConfig, searchParams],
  );

  const setCursor = useCallback(
    (cursor: string | null) => {
      updateUrlParams(filterValues, cursor, currentPerPage);
    },
    [filterValues, currentPerPage, updateUrlParams],
  );

  const setPerPage = useCallback(
    (perPage: string | null) => {
      // Resetting cursor when perPage is updated
      updateUrlParams(filterValues, null, perPage);
    },
    [filterValues, updateUrlParams],
  );

  const updateFilter = useCallback(
    (filterKey: string, newValue: string) => {
      const updatedFilters = filters.map((filter) =>
        filter.key === filterKey
          ? {
              ...filter,
              value: newValue,
            }
          : filter,
      );

      const newFilterValues = getFilterValues(updatedFilters);
      // Resetting cursor when a filter is updated
      setFilterValues(newFilterValues);
      updateUrlParams(newFilterValues, null, currentPerPage);
    },
    [filters, updateUrlParams, currentPerPage, setFilterValues],
  );

  const updateFilters = useCallback(
    (filters: Record<string, string>) => {
      const updatedFilters = Object.entries(filters)
        .map(([filterKey, filterValue]) =>
          filterValue
            ? {
                key: filterKey,
                value: filterValue,
              }
            : undefined,
        )
        .filter(Boolean) as Array<PropertyFilter>;

      const newFilterValues = getFilterValues(updatedFilters);
      // Resetting cursor when filters are updated
      setFilterValues(newFilterValues);
      updateUrlParams(newFilterValues, null, currentPerPage);
    },
    [updateUrlParams, currentPerPage, setFilterValues],
  );

  const clearAllFilters = useCallback(() => {
    // Resetting cursor when filters are updated
    const newFilterValues = getFilterValues(defaultFilterConfig);
    setFilterValues(newFilterValues);
    updateUrlParams(newFilterValues, null, null);
  }, [defaultFilterConfig, setFilterValues, updateUrlParams]);

  return {
    filters,
    filterValues,
    updateFilter,
    updateFilters,
    clearAllFilters,
    cursor: currentCursor,
    setCursor,
    perPage: currentPerPage,
    setPerPage,
  };
};
